import { Flame } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  countTryOnAppointmentsByType,
  getTryOnHeatLevel,
  getTryOnHeatPresentation,
  MeetingWithType,
  TRY_ON_MEETING_TYPES,
  TryOnMeetingType,
} from '../../shared/customerTryOnUtils';
import { meetingTypeToTranslationKey } from '../../shared/meetingTypeToTranslationKey';
import Tooltip from '../Tooltip/Tooltip';

type Props = {
  count: number;
  meetings?: MeetingWithType[] | null;
};

const TryOnHeatBadge = ({ count, meetings }: Props) => {
  const { t } = useTranslation();
  const level = getTryOnHeatLevel(count);
  const presentation = getTryOnHeatPresentation(level);
  const byType = countTryOnAppointmentsByType(meetings);

  const typeBreakdown = TRY_ON_MEETING_TYPES
    .filter((type: TryOnMeetingType) => byType[type] > 0)
    .map((type) => `${byType[type]}× ${t(meetingTypeToTranslationKey(type))}`);

  const tooltipContent =
    count === 0
      ? t('customers.details.try_on_heat.none')
      : (
          <div className="space-y-1 text-left">
            <div>{t('customers.details.try_on_heat.tooltip', { count })}</div>
            {typeBreakdown.length > 0 && (
              <div className="text-gray-300 text-xs">
                {typeBreakdown.join(' · ')}
              </div>
            )}
          </div>
        );

  return (
    <Tooltip content={tooltipContent} position="bottom">
      <span
        className={`inline-flex items-center gap-1 min-w-[2rem] ${presentation.textClass}`}
        aria-label={
          (count === 0
            ? t('customers.details.try_on_heat.none')
            : t('customers.details.try_on_heat.tooltip', { count })) as string
        }
      >
        {presentation.showFlame && (
          <Flame
            className={`w-4 h-4 shrink-0 ${presentation.iconClass}`}
            fill={presentation.fillFlame ? 'currentColor' : 'none'}
            strokeWidth={presentation.fillFlame ? 0 : 2}
          />
        )}
        <span className="text-sm tabular-nums">{count}</span>
      </span>
    </Tooltip>
  );
};

export default TryOnHeatBadge;
